/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import {
  Autocomplete,
  FormControl,
  TextField,
  IconButton,
  Tooltip,
  Grid,
  Box,
  Divider,
  Typography,
  FormLabel,
  Badge,
} from '@mui/material';
import { CustomStack, CustomToggleSwitch } from 'modules/common/components';
import { AddHotelGroup } from 'modules/common/components/add-hotel-group';
import styles from 'modules/dashboard/components/tab-container/inner-filter/inner-filter.module.scss';
import { useEffect, useState } from 'react';
import Apartment from '@mui/icons-material/Apartment';
import { getHotelDemoModeFocusOnValue } from 'modules/dashboard/functions';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StarIcon from '@mui/icons-material/Star';
import { useDispatch, useSelector } from 'react-redux';
import { selectAuthUser, selectOrganizationId } from 'modules/common/auth/selectors';
import { selectHotelGroups, selectIsDemoUser, selectTabValue } from 'modules/dashboard/selectors';
import { dashboardActions } from 'modules/dashboard/slice';
import { Info } from 'react-feather';
import { isEmpty } from 'modules/common/helpers/object';
import { CustomEditIcon } from './style';
import ViewHotelSetOverTime from '../../../../../../common/components/hotel-set-over-time';
/**
 * Compare hotels window component common for the dashboard component
 * @param {*} focusOn - selected hotel
 * @param {Array} hotelList - hotel list
 * @param {*} focusOn - selected compared hotels
 * @param {String} focusOnLabel - label for dropdown
 * @param {Function} handleCompareHotelsChange - Function to set compare hotels value
 * @param {Boolean} isTrendHotelAggregate - state of isTrendHotelAggregate
 * @param {Boolean} aggregateDisabled - state of aggregate toggle enabled/disabled
 * @param {Function} handleTrendHotelAggregate - Function to set trend hotel aggregate value
 * @returns
 */
const CompareHotelsWindow = ({
  hotelList,
  focusOn,
  mergedHotelList,
  focusOnLabel,
  handleCompareHotelsChange,
  normalizedViewToggle,
  handleTrendHotelNormalize,
  enableSecondaryHotelGroups,
  hotelGroupList,
  invalidPrimaryHotels,
  isTrendPercentageChange = false,
  disableNormalizedViewToggle = false,
  id = '',
  isRatePlanEnabled = false,
  originalHotelFocusOn = null,
  isDemoModeActivated = false,
  setOriginalHotelFocusOn = () => {},
  resetFocusOn = () => {},
  isTravelAgentOn = false,
  isRoomNightsPaceEnabled = false,
  hotelSetOverTime,
  handleHotelSetChange = () => {},
  isHotelSetOverTimeDisabled,
}) => {
  const dispatch = useDispatch();
  //
  const organizationId = useSelector(selectOrganizationId);
  const authUser = useSelector(selectAuthUser);
  const selectedHotelGroups = useSelector(selectHotelGroups);
  const isDemoUser = useSelector(selectIsDemoUser);
  const tabValue = useSelector(selectTabValue);
  //
  const [showHotelSetOverTime, setShowHotelSetOverTime] = useState(false);
  const [open, setOpen] = useState(false);
  const [isEditable, setIsEditable] = useState(false);
  const [noOfRooms, setNoOfRooms] = useState(0);
  const [modifiedList, setModifiedList] = useState([]);
  const [addHotelGroupList, setAddHotelGroupList] = useState([]);
  // set the number of rooms per hotel/hotel set
  useEffect(() => {
    setNoOfRooms(0);
    if (!isEmpty(focusOn) && enableSecondaryHotelGroups) {
      setIsEditable(true);
      focusOn?.hotels?.forEach((hotel) => {
        setNoOfRooms((prev) => prev + parseInt(hotel?.roomCapacity, 10));
      });
    } else if (!isEmpty(focusOn)) {
      setIsEditable(true);
      setNoOfRooms((prev) => prev + parseInt(focusOn?.roomCapacity, 10));
    }
  }, [focusOn, enableSecondaryHotelGroups]);
  //
  useEffect(() => {
    if (enableSecondaryHotelGroups) {
      setIsEditable(!focusOn?.isPredefined);
    }
  }, [focusOn, enableSecondaryHotelGroups]);
  //
  const handleAddHotelToFavorites = (e, option) => {
    if (hotelGroupList?.some((group) => group?.label === option?.label)) {
      const favoriteHotelSets = hotelGroupList
        ?.map(() => {
          const matchingGroup = selectedHotelGroups?.results?.find(
            ({ hotelGroups }) => hotelGroups && hotelGroups?.hotelGroupName === option?.id
          );
          return matchingGroup
            ? {
                hotelGroups: [
                  {
                    hotelQuery: matchingGroup?.hotelGroups?.hotelQuery,
                    hotelGroupName: matchingGroup?.hotelGroups?.hotelGroupName,
                    isFavorite: !option?.isFavorite,
                  },
                ],
                userId: authUser?.id,
                organizationId,
              }
            : null;
        })
        ?.filter(Boolean)[0];
      dispatch(dashboardActions.addHotelGroupToFavorites(favoriteHotelSets));
    } else {
      dispatch(
        dashboardActions.addHotelToFavorite({
          organizationId,
          userId: authUser?.id,
          hotelId: option?.id,
        })
      );
    }
  };
  //
  const modifyOptions = (options) => {
    const modifiedOptions = [];

    options?.forEach((option) => {
      if (option.isFavorite && !option?.isPredefined) {
        modifiedOptions.push({ ...option, group: 3 });
      } else if (!option?.isPredefined) {
        modifiedOptions.push({ ...option, group: 1 });
      }
      if (option.isFavorite && !option?.isPredefined) {
        modifiedOptions.push({ ...option, group: 1 });
      }
      if (option?.isPredefined) {
        modifiedOptions.push({ ...option, group: 2 });
      }
    });
    // Sort options first by group, then by label
    return modifiedOptions.sort((a, b) => {
      if (a.group === b.group) {
        if (isDemoUser || (!isDemoUser && isDemoModeActivated)) {
          return Number(a?.label?.split(' ')[1]) - Number(b?.label?.split(' ')[1]);
        }
        return a.label.localeCompare(b.label);
      }
      return b.group - a.group;
    });
  };
  //
  const isValidHotelLabel = (ff) => {
    const hotelPattern = /^Hotel \d+$/;
    return hotelPattern.test(ff.label);
  };
  //
  useEffect(() => {
    if (!isDemoUser && isDemoModeActivated && !isEmpty(focusOn) && !isValidHotelLabel(focusOn)) {
      setOriginalHotelFocusOn(focusOn);
      const demoModeOnFocusOn = {
        ...focusOn,
        label: focusOn?.altName,
      };
      if (!('hotels' in demoModeOnFocusOn)) {
        handleCompareHotelsChange(demoModeOnFocusOn, demoModeOnFocusOn);
      }
    }

    if (!isDemoUser && !isDemoModeActivated && originalHotelFocusOn && isValidHotelLabel(focusOn)) {
      const demoModeOnFocusOff = {
        ...focusOn,
        label: focusOn?.name,
      };
      if (!('hotels' in demoModeOnFocusOff)) {
        resetFocusOn(demoModeOnFocusOff);
      }
    }
  }, [isDemoModeActivated, tabValue]);
  //
  useEffect(() => {
    if (mergedHotelList?.length > 0) {
      const modifiedOptions = modifyOptions(mergedHotelList);
      setModifiedList(modifiedOptions);
    }
  }, [mergedHotelList, isDemoUser, isDemoModeActivated]);
  //
  useEffect(() => {
    if (hotelList !== null && hotelList?.length > 0) {
      setAddHotelGroupList([...hotelList]);
      if (!isDemoUser && isDemoModeActivated && !isValidHotelLabel(hotelList[0])) {
        const demoList = getHotelDemoModeFocusOnValue(isDemoModeActivated, [...hotelList]);
        setAddHotelGroupList(demoList);
      }
      if (
        !isDemoUser &&
        !isDemoModeActivated &&
        addHotelGroupList !== null &&
        addHotelGroupList?.length > 0
      ) {
        const demoList = getHotelDemoModeFocusOnValue(isDemoModeActivated, [...addHotelGroupList]);
        setAddHotelGroupList(demoList);
      }
    }
  }, [isDemoModeActivated, hotelList]);
  //
  return (
    <CustomStack cssClassName={styles.innerFilter}>
      <FormControl>
        <Grid container justifyContent="space-between" wrap="nowrap">
          <Grid item alignContent="center">
            <FormLabel sx={{ fontSize: 13 }}>VS. Hotel</FormLabel>
          </Grid>
          <Grid item>
            <Tooltip title={focusOn && 'hotels' in focusOn && 'Hotel Set Over Time'}>
              <span>
                <IconButton
                  color="primary"
                  variant="contained"
                  onClick={() => focusOn && 'hotels' in focusOn && setShowHotelSetOverTime(true)}
                  disabled={(focusOn && !('hotels' in focusOn)) || isHotelSetOverTimeDisabled}
                  sx={{
                    '&.Mui-disabled': {
                      background: 'transparent',
                    },
                  }}
                >
                  <Badge
                    color="error"
                    variant="dot"
                    invisible={isHotelSetOverTimeDisabled || !(focusOn && 'hotels' in focusOn)}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                  >
                    <Info size="15px" />
                  </Badge>
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Add/Edit Hotel Set">
              <span>
                <IconButton
                  color="primary"
                  variant="contained"
                  onClick={() => setOpen(true)}
                  disabled={isRatePlanEnabled || isTravelAgentOn || isRoomNightsPaceEnabled}
                  sx={{
                    '&.Mui-disabled': {
                      background: 'transparent',
                    },
                    ml: 1,
                  }}
                >
                  <CustomEditIcon size="15px" />
                </IconButton>
              </span>
            </Tooltip>
          </Grid>
        </Grid>
      </FormControl>
      <FormControl>
        <div className="secondary-hotel-dropdown">
          <Autocomplete
            size="small"
            disabled={isRatePlanEnabled || isTravelAgentOn || isRoomNightsPaceEnabled}
            id={`${id}-secondary-hotel-selection`}
            options={modifiedList ?? []}
            getOptionLabel={(option) => (option.label ? option.label : '')}
            groupBy={(option) =>
              // eslint-disable-next-line no-nested-ternary
              option.group === 3 ? 'Favorites' : option.group === 2 ? 'Predefined' : 'NonFavorites'
            }
            renderOption={(props, option) => {
              const { key, ...otherProps } = props;
              return (
                <Grid key={option?.id} container justifyContent="space-between" direction="row">
                  <Grid key={`${option?.id}-set-icons`} item xs={10}>
                    <Box
                      component="li"
                      sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                      {...otherProps}
                    >
                      {option.label}
                      {!option.isPredefined &&
                        hotelGroupList?.some((group) => group.label === option?.label) && (
                          <IconButton>
                            <Tooltip title="Hotel Set">
                              <Apartment />
                            </Tooltip>
                          </IconButton>
                        )}
                      {option.isPredefined && (
                        <IconButton>
                          <Tooltip title="Hotel Set">
                            <Apartment />
                          </Tooltip>
                        </IconButton>
                      )}
                    </Box>
                  </Grid>
                  <Grid key={`${option?.id}-icons`} item xs={2}>
                    {!option.isPredefined && (
                      <IconButton onClick={(e) => handleAddHotelToFavorites(e, option)}>
                        <Tooltip
                          title={option.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                        >
                          {option.isFavorite ? (
                            <StarIcon sx={{ height: 16, width: 16 }} />
                          ) : (
                            <StarBorderIcon sx={{ height: 16, width: 16 }} />
                          )}
                        </Tooltip>
                      </IconButton>
                    )}
                  </Grid>
                </Grid>
              );
            }}
            renderGroup={(params) => {
              const sortedGroups = ['Favorites', 'Predefined', 'NonFavorites'].map((groupName) => {
                if (params.group === groupName && params?.children?.length > 0) {
                  return (
                    <Grid key={`Groups-${params.group}`}>
                      <Grid key="Favorites">
                        {groupName === 'Favorites' && params?.children?.length > 0 && (
                          <>
                            {params.children}
                            <br />
                            <Divider sx={{ bgcolor: '#61616173' }} variant="middle" />
                            <br />
                          </>
                        )}
                      </Grid>
                      <Grid key="Predefined">
                        {groupName === 'Predefined' && params?.children?.length > 0 && (
                          <>
                            <Typography paddingLeft={2} variant="subtitle2">
                              Pre-Defined Hotel Sets
                            </Typography>
                            {params.children}
                            <br />
                            <Divider sx={{ bgcolor: '#61616173' }} variant="middle" />
                            <br />
                          </>
                        )}
                      </Grid>
                      <Grid key="NonFavorites">
                        {groupName === 'NonFavorites' &&
                          params?.children?.length > 0 &&
                          params.children}
                      </Grid>
                    </Grid>
                  );
                }
                return null;
              });
              // Filter out null values and render sorted groups
              return sortedGroups.filter((group) => group !== null);
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(e, value) => {
              handleCompareHotelsChange(e, value);
              setOriginalHotelFocusOn(value);
            }}
            disableClearable
            value={focusOn ?? []}
            renderInput={(params) => (
              <TextField {...params} label={focusOnLabel} size="small" color="primary" />
            )}
            fullWidth
            componentsProps={{
              paper: {
                sx: {
                  width: '100%',
                  whiteSpace: 'unset',
                  wordBreak: 'break-all',
                },
              },
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                padding: 0,
                height: 'fit-content',
              },
            }}
          />
        </div>
      </FormControl>
      <FormControl>
        <Grid
          container
          flexDirection="row"
          justifyContent="space-between"
          wrap="nowrap"
          alignItems="center"
        >
          <CustomToggleSwitch
            label="Normalized - PAR"
            title="Change ‘Total Occupied Rooms’ and ‘Total Revenue’ graphs to display ‘per room’ metrics for the hotels."
            onChange={handleTrendHotelNormalize}
            checked={normalizedViewToggle ?? true}
            disabled={isTrendPercentageChange || disableNormalizedViewToggle}
            id={`${id}-normalized-toggle`}
            className="normalized-toggle"
          />
          <FormLabel sx={{ fontSize: 11, mr: 1 }}>({noOfRooms ?? 0})</FormLabel>
        </Grid>
      </FormControl>
      <AddHotelGroup
        open={open}
        onClose={() => setOpen(false)}
        hotelList={addHotelGroupList ?? []}
        selectedHotelGroupName={focusOn ?? []}
        isEditable={isEditable}
        enableHotelGroups={enableSecondaryHotelGroups}
        isPrimary={false}
      />
      {focusOn && 'hotels' in focusOn && !focusOn.isPredefined && (
        <ViewHotelSetOverTime
          open={showHotelSetOverTime}
          onClose={() => {
            setShowHotelSetOverTime(false);
          }}
          selectedHotels={focusOn}
          invalidPrimaryHotels={invalidPrimaryHotels}
          hotelSetOverTime={hotelSetOverTime}
          handleHotelSetChange={handleHotelSetChange}
        />
      )}
    </CustomStack>
  );
};
//
export default CompareHotelsWindow;
